import { Spinner } from '@cimplico/components';
import { useState } from 'react';
import { useFirmContext } from '../../../providers/FirmProvider';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useJournal } from '../../../queries/use-journal';
import { WorksheetWithAccount } from '../../../types/worksheet';
import ErrorSplashScreen from '../../../pages/ErrorSplashScreen';
import JournalOverview from '../../journal-overview/JournalOverview';
import NewOrEditJournalForm from '../../new-or-edit-journal/NewOrEditJournalForm';

type ActiveWorksheetJournalProps = {
    worksheet: WorksheetWithAccount;
    journalId: string;
    isEditing: boolean;
    onCancel: () => void;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function ActiveWorksheetJournal({ worksheet, journalId, isEditing, onCancel, onXeroError }: ActiveWorksheetJournalProps): JSX.Element {
    const { shortId } = useFirmContext();
    const { id: workpaperId } = useWorkpaperContext();
    const [edit, setEdit] = useState<boolean>(isEditing);

    const journalQuery = useJournal(workpaperId, journalId);

    if (journalQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (journalQuery.isError || !journalQuery.isSuccess) {
        if (journalQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Journal not found."
                    redirectTitle="Back to journals"
                    redirectUrl={`/${shortId}/workpapers/${workpaperId}/journals`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to journals"
                redirectUrl={`/${shortId}/workpapers/${workpaperId}/journals`}
                leftArrow={true}
            />
        );
    }

    const journal = journalQuery.data.journal;

    return (
        <>
            {edit ? (
                <NewOrEditJournalForm
                    worksheet={worksheet}
                    useSmallerStyling
                    defaultJournal={journal}
                    onEdit={(): void => setEdit(false)}
                    onCancel={(): void => {
                        setEdit(false);
                    }}
                    onReturn={onCancel}
                    onXeroError={onXeroError}
                />
            ) : (
                <JournalOverview
                    useSmallerStyling
                    journal={journal}
                    worksheetId={worksheet.id}
                    onClose={onCancel}
                    onXeroError={onXeroError}
                    onEdit={(): void => setEdit(true)}
                />
            )}
        </>
    );
}

export default ActiveWorksheetJournal;
